import dayjs from 'dayjs'
import { useBookStore } from './book'
import { useAirportStore } from './airport'
import packageService from '~/services/packageService'
import Booking from '~/config/Booking'

export const usePackageStore = defineStore('PackageStore', {
  state: () => {
    return {
      package: {},
      options: [],
      type: 'default', // 'default', 'defaultFlightLess', 'defaultHotelLess'
      numberOfPassengers: Booking.defaultNumberOfPassengers,
      duration: null,
      basePrice: null,
      defaultServices: {},
      defaultTotalHotelPrice: null,
      defaultOutwardTransitPrice: null,
      defaultReturnTransitPrice: null,
      defaultOutwardFlightPrice: null,
      defaultReturnFlightPrice: null,
      defaultTicketsPrice: [],
      /* getters.createHotelPackage[] */
      hotels: [],
      outwardFlights: [],
      returnFlights: [],
      events: [],
      isPremiumPackage: false,
      isTransitLessPackage: false,
      isDefaultTransitLessPackage: false,
      isFlightLessPackage: false, // Default service has no flights or there are no available flights for this package (eg: for 4 passengers)
      isDefaultFlightLessPackage: false, // Default service has no flights or user select defaultFlightLess package
      isHotelLessPackage: false,
      isDefaultHotelLessPackage: false,
      isRoundTripPackage: false,
      eventUpgradeFee: 0
    }
  },
  getters: {
    getPackagePrice: state => (packageType) => {
      // Add null checking for package
      if (!state.package) {
        return 0
      }

      let price = state.package.defaultPrice || 0

      if (packageType === 'premium') {
        // premium price is for one person so default will be this
        const premiumPrice = state.package.premiumPrice || 0
        price = (state.package.defaultPrice || 0) + premiumPrice * 2
      }

      if (packageType === 'defaultFlightLess') {
        price = state.package.defaultFlightLessPrice || 0
      }

      if (packageType === 'defaultHotelLess') {
        price = state.package.defaultHotelLessPrice || 0
      }

      if (packageType === 'withTransit') {
        price = state.package.defaultWithTransitPrice || 0
      }

      return price
    },
    // Ticket price is for per person
    // Ticket is only service which  upgrade fee is not included in
    calculatedTicketPrice: state => (ticketId) => {
      const event = state.getEventByTicketId(ticketId)

      // Add null checking for all required data
      if (!event || !state.defaultTicketsPrice || !state.defaultServices?.tickets) {
        return 0
      }

      const defaultTicket = state.defaultTicketsPrice.find(ticket => ticket.eventId === event.id)
      if (!defaultTicket) {
        return 0
      }

      const defaultPrice = parseFloat(defaultTicket.price)
      const ticket = event.tickets.find(ticket => ticket.id === ticketId)

      if (!ticket) {
        return 0
      }

      const upgradeFee = state.defaultServices.tickets.some(
        ticket => ticket.searchParams.ticketId === ticketId
      )
        ? 0
        : (state.eventUpgradeFee || 0)
      const priceDifference =
        parseFloat(ticket.price) + upgradeFee - defaultPrice

      return Math.round(priceDifference)
    },

    calculatedOutwardTransitPrice: state => (currentPrice) => {
      const defaultPrice = parseFloat(state.defaultOutwardTransitPrice || 0)
      const priceDifference = parseFloat(currentPrice || 0) - defaultPrice

      return Math.round(priceDifference)
    },
    calculatedReturnTransitPrice: state => (currentPrice) => {
      const defaultPrice = parseFloat(state.defaultReturnTransitPrice || 0)
      const priceDifference = parseFloat(currentPrice || 0) - defaultPrice

      return Math.round(priceDifference)
    },
    calculatedOutwardFlightPrice: state => (outwardPrice) => {
      const { outwardFlightPrice } = useBookStore().calculateServicesPrice()
      const numberOfPassengers = state.numberOfPassengers || 1
      const defaultPricePerPassenger =
        (outwardFlightPrice || state.defaultOutwardFlightPrice || 0) / numberOfPassengers
      const outwardPricePerPassenger = (outwardPrice || 0) / numberOfPassengers
      const priceDifference =
        outwardPricePerPassenger - defaultPricePerPassenger

      return Math.round(priceDifference)
    },
    calculatedReturnFlightPrice: state => (returnPrice) => {
      const { returnFlightPrice } = useBookStore().calculateServicesPrice()
      const numberOfPassengers = state.numberOfPassengers || 1
      const defaultPricePerPassenger =
        (returnFlightPrice || state.defaultReturnFlightPrice || 0) / numberOfPassengers
      const returnPricePerPassenger = (returnPrice || 0) / numberOfPassengers
      const priceDifference = returnPricePerPassenger - defaultPricePerPassenger

      return Math.round(priceDifference)
    },
    getEventByTicketId: state => (ticketId) => {
      // Add null checking for package and events
      if (!state.package?.events || !Array.isArray(state.package.events)) {
        return null
      }

      return state.package.events.find(event =>
        event.tickets?.map(eventTicket => eventTicket.id).includes(ticketId)
      ) || null
    },
    getVenues: state => () => {
      // Add null checking for package and events
      if (!state.package?.events || !Array.isArray(state.package.events)) {
        return []
      }

      // Also unique venues
      return state.package.events
        .map(event => event.venue)
        .filter(venue => venue) // Filter out null/undefined venues
        .filter(
          (value, index, self) =>
            self.findIndex(m => m?.id === value?.id) === index
        )
    },
    getCities: state => () => {
      // Add null checking for package and events
      if (!state.package?.events || !Array.isArray(state.package.events)) {
        return []
      }

      return state.package.events
        .map(event => event.venue?.city)
        .filter(city => city) // Filter out null/undefined cities
        .filter(
          (value, index, self) =>
            self.findIndex(m => m?.id === value?.id) === index
        )
    },
    createHotelPackage: () => (defaultHotelId, searchedHotels) => {
      return {
        defaultHotelId,
        searchedHotels
      }
    }
  },

  actions: {
    resetPackage () {
      // Reset basic package data
      this.package = {}
      this.options = []
      this.type = 'default'
      this.numberOfPassengers = Booking.defaultNumberOfPassengers
      this.duration = null
      this.basePrice = null
      this.defaultServices = {}
      this.defaultTotalHotelPrice = null
      this.defaultOutwardTransitPrice = null
      this.defaultReturnTransitPrice = null
      this.defaultOutwardFlightPrice = null
      this.defaultReturnFlightPrice = null
      this.defaultTicketsPrice = []

      // Reset arrays
      this.hotels = []
      this.outwardFlights = []
      this.returnFlights = []
      this.events = []

      // Reset boolean flags
      this.isPremiumPackage = false
      this.isTransitLessPackage = false
      this.isDefaultTransitLessPackage = false
      this.isFlightLessPackage = false
      this.isDefaultFlightLessPackage = false
      this.isHotelLessPackage = false
      this.isDefaultHotelLessPackage = false
      this.isRoundTripPackage = false

      // Reset other values
      this.eventUpgradeFee = 0
    },
    async fetch (slug) {
      const { package: packageItems } = await packageService.detail(slug)
      this.package = packageItems
      this.options = packageItems.options
      this.duration = dayjs(packageItems.endDate).diff(
        packageItems.startDate,
        'day'
      )
      this.basePrice = packageItems.basePrice
      this.eventUpgradeFee = packageItems.extraFees.eventUpgradeFee
      this.defaultServices = packageItems.defaultServices
      const defaultTickets = packageItems.defaultServices.tickets.map(
        (ticket) => {
          return {
            eventId: ticket.searchParams.eventId,
            price: ticket.info.price
          }
        }
      )
      this.defaultTicketsPrice = defaultTickets
      // TODO: Should be changed whenever Backend changed the API

      const isHotelLess = packageItems.isHotelLess
      this.isHotelLessPackage = isHotelLess
      this.isDefaultHotelLessPackage = isHotelLess
      const isFlightLess = packageItems.isFlightLess
      this.isFlightLessPackage = isFlightLess
      this.isDefaultFlightLessPackage = isFlightLess
      const isRoundTrip = packageItems.isRoundTrip
      this.isRoundTripPackage = isRoundTrip

      const flightOutwardDefaultPrice = isFlightLess
        ? 0
        : parseFloat(packageItems.defaultServices.flight.info.price)
      const flightReturnDefaultPrice = isFlightLess
        ? 0
        : parseFloat(packageItems.defaultServices.flight.info.return.price)
      this.defaultOutwardFlightPrice = flightOutwardDefaultPrice
      this.defaultReturnFlightPrice = flightReturnDefaultPrice
      this.defaultTotalHotelPrice = isHotelLess
        ? 0
        : packageItems.defaultServices.hotels.reduce((carry, hotel) => {
          carry += hotel.info.rooms[0].price
          return carry
        }, 0)

      // Fill default airports
      if (!isFlightLess) {
        const airportCodes = [
          packageItems.defaultServices.flight.info.originAirport,
          packageItems.defaultServices.flight.info.destinationAirport,
          packageItems.defaultServices.flight.info.return.originAirport,
          packageItems.defaultServices.flight.info.return.destinationAirport
        ]

        await useAirportStore().getAirports(airportCodes)
      }

      return packageItems
    },
    async fetchReturnFlights ({
      returnDate,
      isMinimalSearchMode,
      maxStops,
      country,
      cities
    }) {
      const { getApiPassengerData } = usePassengerData()
      const { adults, children } = getApiPassengerData()

      const { flights: returnFlights } =
        await packageService.availableReturnFlights(
          this.package.slug,
          adults,
          children,
          returnDate,
          isMinimalSearchMode,
          maxStops,
          country,
          cities
        )

      if (!returnFlights.length) {
        // We now search for 1 stop automatically
        const { flights: oneStopReturnFlights } =
          await packageService.availableReturnFlights(
            this.package.slug,
            adults,
            children,
            returnDate,
            isMinimalSearchMode,
            ++maxStops,
            country,
            cities
          )

        this.returnFlights = oneStopReturnFlights
        this.addAirports(oneStopReturnFlights)

        return oneStopReturnFlights

        // throw new Error('Vi har ingen direkte flyvninger. Prøv at tilføj 1 stop (mellemlanding).')
      }

      this.returnFlights = returnFlights
      this.addAirports(returnFlights)

      return returnFlights
    },

    async fetchOutwardFlights ({
      outwardDate,
      isMinimalSearchMode,
      maxStops,
      country,
      cities
    }) {
      const { getApiPassengerData } = usePassengerData()
      const { adults, children } = getApiPassengerData()

      const { flights: outwardFlights } =
        await packageService.availableOutwardFlights(
          this.package.slug,
          adults,
          children,
          outwardDate,
          isMinimalSearchMode,
          maxStops,
          country,
          cities
        )

      if (!outwardFlights.length) {
        // We now search for 1 stop automatically
        const { flights: oneStopOutwardFlights } =
          await packageService.availableOutwardFlights(
            this.package.slug,
            adults,
            children,
            outwardDate,
            isMinimalSearchMode,
            ++maxStops,
            country,
            cities
          )

        this.outwardFlights = oneStopOutwardFlights
        this.addAirports(oneStopOutwardFlights)

        return oneStopOutwardFlights

        // throw new Error('Vi har ingen direkte flyvninger. Prøv at tilføj 1 stop (mellemlanding).')
      }

      this.outwardFlights = outwardFlights
      this.addAirports(outwardFlights)

      return outwardFlights
    },
    async fetchHotels ({
      checkin,
      checkout,
      hotelId: defaultHotelId,
      venueId = null,
      serviceUniqueId = null
    }) {
      const { hotels } = await packageService.availableHotels(
        this.package.slug,
        useBookStore().rooms,
        checkin,
        checkout,
        defaultHotelId,
        venueId,
        serviceUniqueId
      )
      if (!hotels.length) {
        throw new Error('Vi har ikke nogen hoteller til rådighed lige nu.')
      }
      const hotelPackage = this.createHotelPackage(defaultHotelId, hotels)
      const newHotels = this.hotels.filter(
        hotelPackage => hotelPackage.defaultHotelId !== defaultHotelId
      )
      newHotels.push(hotelPackage)
      this.hotels = newHotels

      return hotelPackage
    },
    addAirports (flights) {
      const airportCodes = flights
        .map(flight => flight.segments)
        .reduce((carry, segments) => {
          const segmentsAirportCodes = segments.reduce(
            (segCarry, segment) =>
              segCarry.concat(
                ...[segment.fromAirport.code, segment.toAirport.code]
              ),
            []
          )
          return carry.concat(...segmentsAirportCodes)
        }, [])

      useAirportStore().getAirports(airportCodes)
    }
  }
})
